import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "../site/Reveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const WorkImage = ({ images, alt, code }) => {
  const [idx, setIdx] = useState(0);
  if (!images.length) {
    return (
      <div className="aspect-[16/10] relative bg-gradient-to-tr from-slate-900 via-slate-800 to-blue-900">
        <div className="absolute top-4 left-4 bg-slate-950/90 text-white font-mono text-xs px-3 py-1">{code}</div>
        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs font-mono uppercase tracking-widest">
          Foto di cantiere in arrivo
        </div>
      </div>
    );
  }
  return (
    <div className="aspect-[16/10] relative overflow-hidden group/gallery">
      <img src={images[idx]} alt={`${alt} - foto ${idx + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute top-4 left-4 bg-slate-950/90 text-white font-mono text-xs px-3 py-1">{code}</div>
      {images.length > 1 && (
        <>
          <button
            type="button"
            data-testid="gallery-prev"
            onClick={() => setIdx((idx - 1 + images.length) % images.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-slate-950/80 text-white p-1.5 opacity-0 group-hover/gallery:opacity-100 transition-opacity"
            aria-label="Foto precedente"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            data-testid="gallery-next"
            onClick={() => setIdx((idx + 1) % images.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-slate-950/80 text-white p-1.5 opacity-0 group-hover/gallery:opacity-100 transition-opacity"
            aria-label="Foto successiva"
          >
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-3 right-3 bg-slate-950/80 text-white font-mono text-[10px] px-2 py-0.5">
            {idx + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};

const FALLBACK_WORKS = [
  {
    img: "https://images.unsplash.com/photo-1519143009590-e3800b9df468?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    alt: "Cantiere di recupero strutturale con ponteggi",
    code: "COMMESSA N. 01 · RISTRUTTURAZIONE COMPLESSA",
    title: "Riqualificazione Edilizia Integrata",
    text: "Consolidamento delle strutture portanti, rifacimento facciate e coperture, isolamento a cappotto e adeguamento impiantistico per condomini e stabili residenziali.",
    sector: "Condomini / Residenziale",
    status: "Schede di cantiere in pubblicazione",
  },
  {
    img: "https://images.unsplash.com/photo-1527335988388-b40ee248d80c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    alt: "Nuova costruzione civile con gru e getti di fondazione",
    code: "COMMESSA N. 02 · NUOVA COSTRUZIONE",
    title: "Opere Strutturali & Nuovi Fabbricati",
    text: "Scavi di fondazione, strutture in cemento armato antisismico, tamponamenti e gestione completa delle maestranze di cantiere nel rispetto del cronoprogramma.",
    sector: "Grandi Commesse / Lavori Pubblici",
    status: "Schede di cantiere in pubblicazione",
  },
];

export const TechProjects = () => {
  const [works, setWorks] = useState(FALLBACK_WORKS);

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/cantieri`)
      .then(({ data }) => {
        if (data.length > 0) {
          setWorks(
            data.map((c) => ({
              img: c.image_url ? `${BACKEND}${c.image_url}` : "",
              images: (c.images || []).map((u) => `${BACKEND}${u}`),
              alt: c.title,
              code: [c.code && `COMMESSA N. ${c.code}`, (c.status || "").toUpperCase()].filter(Boolean).join(" · ") || "CANTIERE SAIRCOS",
              title: c.title,
              text: c.description,
              sector: c.sector || "Edilizia",
              status: c.status,
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  return (
  <section id="progetti" data-testid="projects-section" className="py-24 bg-white border-b border-slate-200">
    <div className="max-w-7xl mx-auto px-6">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 pb-6 border-b border-slate-200">
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-blue-600">Archivio Esecutivo</span>
            <h2 className="text-3xl md:text-4xl font-extrabold uppercase text-slate-950 mt-1">Opere Realizzate &amp; Cantieri</h2>
          </div>
          <p className="text-sm text-slate-500 max-w-md mt-4 md:mt-0 font-light">
            Schede tecniche degli interventi Saircos: fotografie e dettagli di cantiere pubblicati e aggiornati
            direttamente dalla nostra direzione.
          </p>
        </div>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {works.map((w, i) => (
          <Reveal key={w.code} delay={i * 0.1}>
            <div data-testid={`work-card-${i}`} className="border border-slate-200 bg-slate-50 flex flex-col justify-between h-full">
              <div>
                <WorkImage images={w.images || (w.img ? [w.img] : [])} alt={w.alt} code={w.code} />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 uppercase mb-2">{w.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{w.text}</p>
                  <div className={`grid ${w.status ? "grid-cols-2" : "grid-cols-1"} gap-4 text-xs font-mono border-t border-slate-200 pt-4 text-slate-600`}>
                    <div>Settore: <span className="font-bold text-slate-900">{w.sector}</span></div>
                    {w.status && <div>Stato: <span className="font-bold text-blue-600">{w.status}</span></div>}
                  </div>
                </div>
              </div>
              <div className="p-6 pt-0">
                <a href="#contact" data-testid={`work-cta-${i}`} className="inline-block text-xs font-bold font-mono uppercase text-blue-600 hover:text-blue-800 transition-colors">
                  Richiedi scheda tecnica di cantiere →
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};
